/**
 * Duplicate claimant/claim checks (aligned with admin portal warnings).
 */

export async function fetchClaimantNameDuplicate(makeAuthenticatedRequest, firstName, lastName, excludeId = null) {
  const fn = (firstName || '').trim();
  const ln = (lastName || '').trim();
  if (!fn || !ln) return null;

  const params = new URLSearchParams({
    first_name: fn,
    last_name: ln
  });
  if (excludeId) params.set('exclude_id', String(excludeId));

  try {
    const data = await makeAuthenticatedRequest(`/claimants/check-name?${params.toString()}`);
    if (data?.success && data.exists) {
      return { total: data.total, matches: data.matches || [] };
    }
    return null;
  } catch (error) {
    console.error('Claimant duplicate check failed:', error);
    return null;
  }
}

/** Run duplicate claimant check and show the warning modal when appropriate. */
export async function runClaimantNameDuplicateCheck(
  makeAuthenticatedRequest,
  setDuplicateModal,
  setWarning,
  firstName,
  lastName,
  excludeId = null
) {
  const warning = await fetchClaimantNameDuplicate(
    makeAuthenticatedRequest,
    firstName,
    lastName,
    excludeId
  );
  if (setWarning) setWarning(warning);
  showDuplicateAlert(setDuplicateModal, 'claimant', warning);
  return warning;
}

export async function fetchClaimNumberDuplicate(makeAuthenticatedRequest, claimNumber, excludeId = null) {
  const num = (claimNumber || '').trim();
  if (!num) return null;

  const exclude = excludeId ? `&exclude_id=${excludeId}` : '';
  try {
    const data = await makeAuthenticatedRequest(
      `/claims/check-claim-number?claim_number=${encodeURIComponent(num)}${exclude}`
    );
    if (data?.success && data.exists && data.claim) {
      return data.claim;
    }
    return null;
  } catch {
    return null;
  }
}

/** Show informational popup when a duplicate is detected (e.g. on blur). */
export function showDuplicateAlert(setDuplicateModal, type, data) {
  if (!data) {
    setDuplicateModal(null);
    return;
  }
  setDuplicateModal({ type, data, mode: 'alert' });
}

/** Show confirm popup; resolves true if user continues, false if cancelled. */
export function requestDuplicateConfirm(setDuplicateModal, type, data) {
  if (!data) return Promise.resolve(true);
  return new Promise((resolve) => {
    setDuplicateModal({
      type,
      data,
      mode: 'confirm',
      onResolve: resolve
    });
  });
}

/** @deprecated Use requestDuplicateConfirm — kept for gradual migration */
export function confirmDuplicateClaimantName(warning) {
  if (!warning) return true;
  return window.confirm(
    `A claimant with this name already exists (${warning.total} match${warning.total !== 1 ? 'es' : ''}). ` +
      'Create this claimant anyway if it is a different person?'
  );
}

/** @deprecated Use requestDuplicateConfirm */
export function confirmDuplicateClaimNumber(claim) {
  if (!claim) return true;
  return window.confirm(
    `This claim number already exists (${claim.case_type} for ${claim.claimant_name}, Claim #${claim.id}). ` +
      'Create this claim anyway?'
  );
}
